import { Flex } from 'antd';
import { CustomShipmentText, CustomTitle } from '@/components';
import TrackingStatusCard from './TrackingStatusCard';
import type {
  SingleShipmentDataExtended,
  ShipmentLegDetail,
} from '@/apis/types';
import { ChevronRightIcon } from '@/assets/icons/icons';

interface ShipmentLegsListProps {
  shipment: SingleShipmentDataExtended | null | undefined;
  onSeeAll?: () => void;
  onLegClick?: (leg: ShipmentLegDetail) => void;
}

export default function ShipmentLegsList({
  shipment,
  onSeeAll,
  onLegClick,
}: ShipmentLegsListProps) {
  const legsInfo = shipment?.legs_records;
  const legsArray: ShipmentLegDetail[] =
    legsInfo && Array.isArray(legsInfo) ? legsInfo : [];
  const legCount = shipment?.leg_count;
  const legType = shipment?.leg_type;
  const activeExecutionLeg = shipment?.active_execution_leg;
  const activeExecutionLegId =
    typeof activeExecutionLeg === 'string'
      ? activeExecutionLeg
      : activeExecutionLeg?.id;

  if (legsArray.length === 0) return null;

  return (
    <Flex vertical gap={8} style={{ marginBottom: '16px' }}>
      <Flex justify='space-between' align='center' style={{ width: '100%' }}>
        <CustomTitle
          text={
            legType === 'CL'
              ? `Execution legs (${legCount})`
              : `Customer leg (${legCount})`
          }
          fontSize={18}
          fontWeight={700}
          lineHeight='24px'
          marginBottom='0'
        />
        {Number(legCount || 0) > 5 && (
          <button
            type='button'
            aria-label={`See all ${legType === 'CL' ? 'execution' : 'customer'} legs`}
            className='c-pointer shipment-legs-see-all-button'
            onClick={onSeeAll}
          >
            <Flex align='center' gap={8}>
              <CustomShipmentText
                text='See all'
                color='var(--text-primary)'
                fontWeight={600}
              />
              <ChevronRightIcon
                fill='var(--text-primary)'
                width={16}
                height={16}
              />
            </Flex>
          </button>
        )}
      </Flex>
      <Flex vertical gap={8}>
        {legsArray.slice(0, 5).map((leg: ShipmentLegDetail, idx: number) => {
          const normalizedLeg = {
            ...leg,
            estimated_delivery_date:
              leg.etas?.estimated_delivery_date || undefined,
            courier_name:
              leg.transport_provider?.title || leg.carrier_name || undefined,
          };
          return (
            <TrackingStatusCard
              key={leg.id || idx}
              legDetail={normalizedLeg}
              isActive={Boolean(
                activeExecutionLegId && leg.id === activeExecutionLegId
              )}
              onClick={onLegClick ? () => onLegClick(leg) : undefined}
            />
          );
        })}
      </Flex>
    </Flex>
  );
}
